import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BadgeCheck, MapPin, Plane, Weight, Clock, Star } from "lucide-react";
import { Rating, VerifiedBadge, Spinner } from "../components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import RequestItemDialog from "../components/RequestItemDialog";
import api from "../lib/api";
import { rupiah, fmtDate } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

export default function BawainerProfile() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [trips, setTrips] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reqTrip, setReqTrip] = useState(null);

  useEffect(() => {
    api.get("/trips").then((r) => {
      const all = r.data.filter((tr) => tr.bawainer?.id === id);
      setTrips(all);
      if (all[0]) setProfile(all[0].bawainer);
    });
    api.get(`/reviews/user/${id}`).then((r) => setReviews(r.data));
    api.get(`/trips?sort=boosted`).then((r) => {
      const m = r.data.find((tr) => tr.bawainer?.id === id);
      if (m) setProfile(m.bawainer);
    });
  }, [id]);

  if (!profile) return <Spinner label={t("loading")} />;

  const catList = profile.categories || [];

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl p-6 shadow-soft border border-black/5">
        <div className="flex items-start gap-4">
          <img src={profile.avatar} alt={profile.name} className="w-20 h-20 rounded-2xl object-cover" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-heading font-extrabold text-2xl text-navy">{profile.name}</h1>
              <VerifiedBadge status={profile.verified_status} />
            </div>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
              <Rating value={profile.rating} count={profile.reviews_count} />
              <span>{profile.completed_trips} {t("completed_trips")}</span>
              <span>{profile.response_rate}% {t("response_rate")}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{profile.bio}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-5">
          {[["Rating", profile.rating?.toFixed(1) || "—"], ["Trips", profile.completed_trips], ["Response", profile.response_time]].map(([l, v]) => (
            <div key={l} className="bg-cloud rounded-2xl p-3 text-center">
              <div className="font-heading font-bold text-navy">{v}</div>
              <div className="text-[11px] text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
      </div>

      <Tabs defaultValue="trips">
        <TabsList className="rounded-full bg-white border border-black/5 p-1">
          <TabsTrigger value="trips" data-testid="tab-trips" className="rounded-full">Upcoming Trips</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-full">Reviews ({reviews.length})</TabsTrigger>
          <TabsTrigger value="about" data-testid="tab-about" className="rounded-full">About</TabsTrigger>
        </TabsList>

        <TabsContent value="trips" className="space-y-3">
          {trips.length === 0 && <p className="text-sm text-muted-foreground py-6">Belum ada trip aktif.</p>}
          {trips.map((tr) => (
            <div key={tr.id} className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
              <div className="flex items-center gap-2 font-heading font-bold text-navy">
                <MapPin size={16} className="text-sky" /> {tr.from_city} → {tr.to_city}
              </div>
              <div className="mt-2 text-sm text-muted-foreground flex flex-wrap gap-4">
                <span className="flex items-center gap-1"><Plane size={14} /> {fmtDate(tr.departure_date)} → {fmtDate(tr.return_date)}</span>
                <span className="flex items-center gap-1"><Weight size={14} /> {tr.capacity_kg} kg</span>
                <span className="font-semibold text-ink">{rupiah(tr.service_fee)}/item</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(tr.categories || []).map((c) => <span key={c} className="text-[11px] bg-cloud rounded-full px-2 py-0.5 text-navy font-medium">{c}</span>)}
              </div>
              {user?.role === "titiper" && (
                <button onClick={() => setReqTrip(tr)} data-testid={`profile-request-${tr.id}`}
                  className="mt-4 w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-2.5 text-sm transition-colors">
                  {t("request_item")}
                </button>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="reviews" className="space-y-3">
          {reviews.length === 0 && <p className="text-sm text-muted-foreground py-6">Belum ada review.</p>}
          {reviews.map((rv) => (
            <div key={rv.id} className="bg-white rounded-3xl p-4 shadow-soft border border-black/5">
              <div className="flex items-center gap-2">
                <img src={rv.reviewer?.avatar} alt="" className="w-8 h-8 rounded-full" />
                <span className="font-semibold text-sm text-navy">{rv.reviewer?.name}</span>
                <span className="ml-auto flex items-center gap-0.5 text-lime">
                  {Array.from({ length: rv.rating }).map((_, i) => <Star key={i} size={13} className="fill-lime" />)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{rv.comment}</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="about">
          <div className="bg-white rounded-3xl p-5 shadow-soft border border-black/5 space-y-4">
            <div>
              <div className="font-semibold text-navy text-sm mb-2">{t("accepted_cats")}</div>
              <div className="flex flex-wrap gap-1.5">
                {catList.map((c) => <span key={c} className="text-xs bg-cloud rounded-full px-3 py-1 text-navy font-medium">{c}</span>)}
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-navy">
              <BadgeCheck size={16} className="text-sky" /> Membership: <span className="font-bold uppercase">{profile.membership_tier}</span>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {reqTrip && <RequestItemDialog trip={reqTrip} open={!!reqTrip} onOpenChange={(v) => !v && setReqTrip(null)} />}
    </div>
  );
}
